import { parseLookupResponse } from '../../services/signup';
import type { SignupLookupResponse, SignupSport } from '../../types/signup';

/**
 * Keeps the signup form across a refresh of the verify step, and across tabs.
 *
 * localStorage rather than sessionStorage, because the Firebase session it pairs with is shared by
 * every tab: a per-tab copy meant opening `/signup` in a second tab (the verification link does
 * exactly that) found a session it could not resume and signed the first tab out. It is bound to the
 * Firebase account's address (`loadSignup` drops a blob for any other), cleared when the program is
 * created and on every sign-out (`AuthContext.logout`), and **never holds the password**: by the
 * verify step the Firebase account already exists and the session is Firebase's own, so there is
 * nothing a password would be needed for.
 *
 * Every access is wrapped: blocked site data degrades to "no resume", never to a broken page.
 */

const STORAGE_KEY = 'rosterpilot.signup';

export interface StoredSignup {
  step: 'verify';
  email: string;
  firstName: string;
  lastName: string;
  sport: SignupSport;
  /**
   * The plan key the coach chose, for a sport priced by plan (baseball). Absent otherwise, and in a
   * blob from a build before plans, which then sends none and gets the cheapest plan.
   */
  planTier?: string;
  /** The school name the coach typed, when the lookup did not match their school. Absent otherwise. */
  schoolName?: string;
  /**
   * The account step cannot be passed without agreeing to the terms, so a stored signup always
   * carries it. A blob without it (from a build before the checkbox) is dropped, so the coach goes
   * back through the account step and agrees.
   */
  acceptedTerms: true;
  lookup: SignupLookupResponse;
}

const isSport = (value: unknown): value is SignupSport =>
  value === 'baseball' || value === 'softball';

export const saveSignup = (state: StoredSignup): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable: a refresh will start over, which is safe.
  }
};

export const clearSignup = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

/**
 * The stored signup, only when it belongs to `email` (the signed-in Firebase account). A blob for
 * any other address is discarded rather than resumed.
 */
export const loadSignup = (email: string | null | undefined): StoredSignup | null => {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof StoredSignup, unknown>> | null;
    // The lookup is read through the same normaliser as the API's answer, and its sports must be a
    // list of sports this build knows: a blob from an older build (or hand-edited) is dropped rather
    // than rendered half-understood.
    const storedSports = (parsed?.lookup as { sports?: unknown } | null | undefined)?.sports;
    const lookup = parseLookupResponse(parsed?.lookup);
    if (
      parsed?.step === 'verify' &&
      typeof parsed.email === 'string' &&
      typeof parsed.firstName === 'string' &&
      typeof parsed.lastName === 'string' &&
      isSport(parsed.sport) &&
      parsed.acceptedTerms === true &&
      lookup?.status === 'eligible' &&
      Array.isArray(storedSports) &&
      storedSports.every(isSport) &&
      email &&
      parsed.email.toLowerCase() === email.toLowerCase()
    ) {
      return {
        step: 'verify',
        email: parsed.email,
        firstName: parsed.firstName,
        lastName: parsed.lastName,
        sport: parsed.sport,
        ...(typeof parsed.planTier === 'string' && parsed.planTier.trim()
          ? { planTier: parsed.planTier }
          : {}),
        ...(typeof parsed.schoolName === 'string' && parsed.schoolName.trim()
          ? { schoolName: parsed.schoolName }
          : {}),
        acceptedTerms: true,
        lookup,
      };
    }
  } catch {
    // Malformed: fall through and drop it.
  }

  clearSignup();
  return null;
};
